import React, { useState, useRef, useEffect } from 'react';
import { keyframes } from '@emotion/react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Chip
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ai-chatbot-358159227527.us-central1.run.app';
const HUBSPOT_MEETING_URL = 'https://meetings.hubspot.com/seifu/introduction-to-kaytics-30min';
const TEAL = '#1FBDBD';
const TEAL_DARK = '#17A0A0';
const BLUE = '#4A8FD9';
const BLUE_DARK = '#3A78C2';

// --- Animations ---
const blink = keyframes`
  0%, 80%, 100% { transform: scaleY(1); }
  85%            { transform: scaleY(0.06); }
  90%            { transform: scaleY(1); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(31,189,189,0.4); }
  50%       { box-shadow: 0 0 0 10px rgba(31,189,189,0); }
`;

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const dotBounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); }
  40%           { transform: translateY(-5px); }
`;

// --- Robot Avatar (Winfield-style) ---
const RobotAvatar = ({ size = 140 }) => (
  <Box sx={{ position: 'relative', width: size, height: size, mx: 'auto' }}>
    {/* Outer blue glow ring */}
    <Box sx={{
      position: 'absolute', inset: -4,
      borderRadius: '50%',
      background: `radial-gradient(circle, rgba(100,181,246,0.2) 0%, transparent 70%)`,
      animation: `${pulse} 2.5s ease-in-out infinite`,
    }} />
    {/* Light blue sphere */}
    <Box sx={{
      width: size, height: size, borderRadius: '50%',
      background: 'radial-gradient(circle at 38% 32%, #e8f1ff 0%, #a8c8fa 50%, #6ea8f0 100%)',
      boxShadow: `0 8px 28px rgba(80,140,220,0.4), inset 0 -4px 12px rgba(0,0,0,0.08)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Shine */}
      <Box sx={{
        position: 'absolute', top: '12%', left: '20%',
        width: '28%', height: '16%',
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.55)',
        transform: 'rotate(-20deg)',
      }} />
      {/* Dark face circle */}
      <Box sx={{
        width: size * 0.56, height: size * 0.56,
        borderRadius: '50%',
        background: 'linear-gradient(145deg, #1a1f1f 0%, #0d1212 100%)',
        boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.5)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: `${size * 0.035}px`,
      }}>
        {/* Eyes row */}
        <Box sx={{ display: 'flex', gap: `${size * 0.1}px`, alignItems: 'center' }}>
          {/* Left eye */}
          <Box sx={{
            width: size * 0.13, height: size * 0.165,
            borderRadius: '50%',
            background: 'radial-gradient(circle at 35% 30%, #ffffff, #d0e8ff)',
            boxShadow: `0 0 ${size * 0.06}px rgba(255,255,255,0.7)`,
            animation: `${blink} 3.8s ease-in-out infinite`,
            transformOrigin: 'center',
          }} />
          {/* Right eye */}
          <Box sx={{
            width: size * 0.13, height: size * 0.165,
            borderRadius: '50%',
            background: 'radial-gradient(circle at 35% 30%, #ffffff, #d0e8ff)',
            boxShadow: `0 0 ${size * 0.06}px rgba(255,255,255,0.7)`,
            animation: `${blink} 3.8s ease-in-out infinite 0.07s`,
            transformOrigin: 'center',
          }} />
        </Box>
        {/* Smile */}
        <Box sx={{
          width: size * 0.18, height: size * 0.06,
          borderRadius: '0 0 50px 50px',
          border: `${size * 0.018}px solid rgba(255,255,255,0.4)`,
          borderTop: 'none',
          mt: `${size * 0.01}px`,
        }} />
      </Box>
    </Box>
  </Box>
);

// --- Suggestion chips ---
const SUGGESTIONS = ['What does Kaytics do?', 'Schedule a call', 'CPG analytics', 'AI services'];

// --- Keyword helpers ---
const SCHEDULE_KEYWORDS = ['schedule','book','booking','appointment','meeting','consultation',
  'consult','calendar','call','demo','talk','speak','chat with','connect with',
  'get started','sign up','when can','available','availability'];
const CONTACT_KEYWORDS = ['live person','real person','human','agent','someone','speak to someone',
  'talk to someone','send email','send an email','email you','email us','contact you',
  'contact us','reach out','get in touch','message you','message the team','sales',
  'support','help me','need help','more info','more information'];

const isSchedulingRequest = m => SCHEDULE_KEYWORDS.some(kw => m.toLowerCase().includes(kw));
const isContactRequest    = m => CONTACT_KEYWORDS.some(kw => m.toLowerCase().includes(kw));

function getSessionId() {
  let id = sessionStorage.getItem('kaya_session_id');
  if (!id) {
    id = 'sess_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);
    sessionStorage.setItem('kaya_session_id', id);
  }
  return id;
}

// ===================== Main Component =====================
const Chatbot = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [openEmail, setOpenEmail] = useState(false);
  const [openSchedule, setOpenSchedule] = useState(false);
  const [emailForm, setEmailForm] = useState({ name: '', email: '', reason: '' });
  const [emailErrors, setEmailErrors] = useState({});
  const [emailSending, setEmailSending] = useState(false);
  const sessionId = useRef(getSessionId());
  const messagesEndRef = useRef(null);

  const hasUserMessages = messages.some(m => m.sender === 'user');

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    setMessages([{ text: "Hi, I'm Kaya, your Kaytics AI assistant. Ask me anything about Kaytics!", sender: 'bot' }]);
  }, []);

  const addBotMessage = (text, scheduleButton = false) =>
    setMessages(prev => [...prev, { text, sender: 'bot', scheduleButton }]);

  const handleOpenEmail = () => {
    setEmailForm({ name: '', email: '', reason: '' });
    setEmailErrors({});
    setOpenEmail(true);
  };

  const validateEmail = () => {
    const e = {};
    if (!emailForm.name.trim()) e.name = 'Name is required';
    if (!emailForm.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailForm.email)) e.email = 'Enter a valid email';
    if (!emailForm.reason.trim()) e.reason = 'Please tell us why you are reaching out';
    return e;
  };

  const sendMessage = async (text) => {
    const userMessage = text.trim();
    if (!userMessage) return;
    setInput('');
    setMessages(prev => [...prev, { text: userMessage, sender: 'user' }]);
    setLoading(true);

    try {
      if (isSchedulingRequest(userMessage)) {
        setLoading(false);
        addBotMessage("I'd love to help you schedule a meeting! You can book a free 30-minute intro call at a time that works for you.", true);
        return;
      }
      if (isContactRequest(userMessage)) {
        setLoading(false);
        handleOpenEmail();
        return;
      }

      const response = await axios.post(`${API_BASE_URL}/api/chat`, {
        message: userMessage,
        sessionId: sessionId.current
      });
      if (response.data.error) throw new Error(response.data.error);
      addBotMessage(response.data.response);
    } catch (error) {
      addBotMessage(error.response?.data?.error || 'Sorry, I encountered an error. Please try again.');
    }
    setLoading(false);
  };

  const handleEmailConnect = async () => {
    const errors = validateEmail();
    if (Object.keys(errors).length > 0) { setEmailErrors(errors); return; }
    setEmailSending(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/connect-email`, {
        name: emailForm.name, email: emailForm.email, reason: emailForm.reason,
        conversationHistory: messages.map(m => `${m.sender === 'user' ? 'User' : 'Kaya'}: ${m.text}`).join('\n')
      });
      if (response.data.error) throw new Error(response.data.error);
      setOpenEmail(false);
      addBotMessage(`Thanks, ${emailForm.name}! Your message has been sent. We'll be in touch at ${emailForm.email} shortly.`);
    } catch (error) {
      addBotMessage(error.response?.data?.error || "Sorry, I couldn't send your message. Please email us at info@kaytics.com.");
    }
    setEmailSending(false);
  };

  return (
    <Box sx={{
      position: 'fixed', bottom: 20, right: 20,
      width: 380, height: 580,
      display: 'flex', flexDirection: 'column',
      bgcolor: '#f8faff',
      boxShadow: '0 16px 48px rgba(0,0,0,0.14), 0 4px 16px rgba(80,140,220,0.15)',
      borderRadius: 4,
      overflow: 'hidden',
      border: '1px solid rgba(100,160,240,0.15)',
    }}>

      {/* Top area — logo + robot */}
      <Box sx={{
        background: 'linear-gradient(180deg, #e8f2ff 0%, #f8faff 100%)',
        pt: 2.5, pb: 1.5,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        borderBottom: hasUserMessages ? '1px solid rgba(31,189,189,0.12)' : 'none',
        flexShrink: 0,
      }}>
        {/* Action buttons top-right */}
        <Box sx={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={() => setOpenSchedule(true)} title="Schedule a meeting"
            sx={{ color: BLUE, '&:hover': { bgcolor: 'rgba(74,143,217,0.1)' } }}>
            <CalendarMonthIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleOpenEmail} title="Contact us"
            sx={{ color: BLUE, '&:hover': { bgcolor: 'rgba(74,143,217,0.1)' } }}>
            <EmailIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Kaytics logo */}
        <Box sx={{ mb: 1 }}>
          <img
            src="/kaytics-logo.png"
            alt="Kaytics"
            style={{ height: 28, width: 'auto', mixBlendMode: 'multiply', display: 'block' }}
          />
        </Box>

        {/* Robot */}
        <RobotAvatar size={hasUserMessages ? 65 : 110} />

        {!hasUserMessages && (
          <Box sx={{ textAlign: 'center', mt: 1.5, animation: `${fadeSlideIn} 0.4s ease` }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a3333' }}>
              Hi, I'm Kaya! 👋
            </Typography>
            <Typography variant="caption" sx={{ color: '#5a8a8a', display: 'block', mt: 0.3 }}>
              Ask me anything about Kaytics
            </Typography>
            {/* Online dot */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.8 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#64b5f6',
                boxShadow: '0 0 5px #64b5f6', animation: `${pulse} 2s infinite` }} />
              <Typography variant="caption" sx={{ color: '#5a8a8a', fontSize: '0.68rem' }}>Online</Typography>
            </Box>
          </Box>
        )}

        {/* Suggestion chips — only on welcome */}
        {!hasUserMessages && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.8, justifyContent: 'center', px: 2 }}>
            {SUGGESTIONS.map(s => (
              <Chip key={s} label={s} size="small" onClick={() => sendMessage(s)} sx={{
                bgcolor: 'white', border: `1.5px solid ${TEAL}`, color: TEAL_DARK,
                fontWeight: 500, cursor: 'pointer', fontSize: '0.72rem',
                '&:hover': { bgcolor: 'rgba(31,189,189,0.08)' },
              }} />
            ))}
          </Box>
        )}
      </Box>

      {/* Messages */}
      {hasUserMessages && (
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
          {messages.map((msg, i) => (
            <Box key={i} sx={{
              display: 'flex', flexDirection: 'column',
              alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start',
              animation: `${fadeSlideIn} 0.22s ease`,
            }}>
              <Box sx={{
                px: 1.6, py: 1, maxWidth: '80%', borderRadius:
                  msg.sender === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                bgcolor: msg.sender === 'user'
                  ? BLUE
                  : 'white',
                boxShadow: msg.sender === 'user'
                  ? '0 3px 10px rgba(74,143,217,0.35)'
                  : '0 1px 4px rgba(0,0,0,0.08)',
                border: msg.sender === 'bot' ? '1px solid rgba(31,189,189,0.15)' : 'none',
              }}>
                <Typography variant="body2" sx={{
                  color: msg.sender === 'user' ? 'white' : '#1a3333',
                  lineHeight: 1.6, fontSize: '0.85rem',
                }}>
                  {msg.text}
                </Typography>
              </Box>
              {msg.scheduleButton && (
                <Button variant="contained" size="small" startIcon={<CalendarMonthIcon />}
                  onClick={() => setOpenSchedule(true)}
                  sx={{ mt: 0.8, textTransform: 'none', borderRadius: 2, fontSize: '0.78rem',
                    bgcolor: BLUE, '&:hover': { bgcolor: BLUE_DARK } }}>
                  Book a Free 30-Min Call
                </Button>
              )}
            </Box>
          ))}
          {loading && (
            <Box sx={{ display: 'flex', gap: 0.6, px: 1.6, py: 1.2, bgcolor: 'white',
              borderRadius: '16px 16px 16px 4px', width: 'fit-content',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid rgba(31,189,189,0.15)' }}>
              {[0, 0.18, 0.36].map(d => (
                <Box key={d} sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: TEAL,
                  animation: `${dotBounce} 1s ease-in-out infinite`, animationDelay: `${d}s` }} />
              ))}
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>
      )}

      {/* Input bar */}
      <Box sx={{
        px: 2, py: 1.2,
        borderTop: '1px solid rgba(31,189,189,0.12)',
        bgcolor: 'white',
        display: 'flex', gap: 1, alignItems: 'center',
      }}>
        <TextField
          fullWidth size="small" value={input}
          onChange={e => setInput(e.target.value)}
          onKeyPress={e => e.key === 'Enter' && sendMessage(input)}
          placeholder="Type or ask anything…"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3, fontSize: '0.85rem', bgcolor: '#f8faff',
              '& fieldset': { borderColor: 'rgba(100,160,240,0.25)' },
              '&:hover fieldset': { borderColor: BLUE },
              '&.Mui-focused fieldset': { borderColor: BLUE },
            }
          }}
        />
        <IconButton onClick={() => sendMessage(input)} disabled={loading || !input.trim()} sx={{
          width: 40, height: 40, borderRadius: 2, flexShrink: 0,
          bgcolor: BLUE, color: 'white',
          '&:hover': { bgcolor: BLUE_DARK },
          '&.Mui-disabled': { bgcolor: 'rgba(74,143,217,0.25)', color: 'white' },
        }}>
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
      <Typography variant="caption" sx={{ textAlign: 'center', pb: 0.8, color: '#9ab8b8', fontSize: '0.62rem', bgcolor: 'white' }}>
        Powered by Kaytics AI
      </Typography>

      {/* HubSpot Scheduler Dialog */}
      <Dialog open={openSchedule} onClose={() => setOpenSchedule(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { height: '80vh', borderRadius: 3 } }}>
        <DialogTitle sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: `linear-gradient(135deg, ${TEAL}, ${TEAL_DARK})`, color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonthIcon />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Book a Free 30-Min Intro Call</Typography>
          </Box>
          <IconButton onClick={() => setOpenSchedule(false)} sx={{ color: 'white' }} size="small">
            <Typography>✕</Typography>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <iframe src={HUBSPOT_MEETING_URL} title="Schedule a meeting with Kaytics"
            width="100%" height="100%" frameBorder="0" style={{ display: 'block', minHeight: '500px' }} />
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={openEmail} onClose={() => setOpenEmail(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <RobotAvatar size={44} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a3333' }}>Contact the Kaytics Team</Typography>
              <Typography variant="caption" sx={{ color: '#5a8a8a' }}>We typically respond within one business day.</Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider sx={{ mt: 2 }} />
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[
            { key: 'name', label: 'Your Name', placeholder: 'Jane Smith', type: 'text' },
            { key: 'email', label: 'Email Address', placeholder: 'jane@company.com', type: 'email' },
          ].map(f => (
            <TextField key={f.key} label={f.label} type={f.type} fullWidth
              value={emailForm[f.key]}
              onChange={e => setEmailForm(p => ({ ...p, [f.key]: e.target.value }))}
              error={!!emailErrors[f.key]} helperText={emailErrors[f.key]}
              placeholder={f.placeholder}
              sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: TEAL } }}
            />
          ))}
          <TextField label="Reason for Contact" fullWidth multiline rows={3}
            value={emailForm.reason}
            onChange={e => setEmailForm(p => ({ ...p, reason: e.target.value }))}
            error={!!emailErrors.reason} helperText={emailErrors.reason}
            placeholder="Tell us how we can help you…"
            sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: TEAL } }}
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => setOpenEmail(false)} color="inherit" disabled={emailSending}>Cancel</Button>
          <Button onClick={handleEmailConnect} variant="contained" disabled={emailSending}
            startIcon={emailSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK }, borderRadius: 2 }}>
            {emailSending ? 'Sending…' : 'Send Message'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Chatbot;
